<script setup>
import TableCell from "@/components/global-themed/table/TableCell.vue";
import {ref, watch} from "vue";
import ExpandableTableRow from "@/components/global-themed/table/ExpandableTableRow.vue";
import AuthorityTag from "@/components/competition/partials/backlinks/AuthorityTag.vue";
import useActiveTab from "@/composables/backlinks/opportunity/useActiveTab";
import LinkExample from "@/components/backlinks/opportunity/partials/LinkExample.vue";
import MoveToRemovedButton from "@/components/backlinks/opportunity/partials/table/cells/MoveToRemovedButton.vue";
import MoveToCompleteButton from "@/components/backlinks/opportunity/partials/table/cells/MoveToCompleteButton.vue";
import MoveToInProgressButton from "@/components/backlinks/opportunity/partials/table/cells/MoveToInProgressButton.vue";
import MoveToOpportunitiesButton from "@/components/backlinks/opportunity/partials/table/cells/MoveToOpportunitiesButton.vue";
import ManuallyCompleteButton from "@/components/backlinks/opportunity/partials/table/cells/ManuallyCompleteButton.vue";
import {useMutation, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";

const props = defineProps({
  backlink: {
    type: Object,
    required: true,
  },
  isExpanded: {
    type: Boolean,
    default: false,
  },
});

const {isTabOpportunities, isTabInProgress, isTabCompleted, isTabRemoved} = useActiveTab();

const showDetails = ref(props.isExpanded);
const emit = defineEmits(['toggle']);
const loadingStatus = ref(null);

watch(() => props.isExpanded, (newVal) => {
  showDetails.value = newVal;
});

const toggleDetails = () => {
  emit('toggle', props.backlink);
};

const queryClient = useQueryClient();

const {mutate: updateOpportunityStatus, isPending} = useMutation( {
  onMutate: async(status) => {
    loadingStatus.value = status;
  },
  mutationFn: async (status) =>  await axios.patch(`/ajax/v2/backlinks/opportunity/${props.backlink.id}/update`, {
    status: status,
  }),
  onSuccess: () => {
    queryClient.invalidateQueries(['backlink-opportunity'])
  },
  onSettled: () => {
    loadingStatus.value = null;
  },
});
</script>

<template>
  <ExpandableTableRow :hide-divider="showDetails">
    <TableCell
      col-span="1"
      class="tw-flex tw-justify-center"
      @click="toggleDetails"
    >
      <AuthorityTag :value="backlink.score" />
    </TableCell>

    <TableCell
      col-span="3"
    >
      <span class="tw-truncate tw-w-auto tw-block">
        {{ backlink.domain }}
      </span>
    </TableCell>

    <TableCell
      :col-span="isTabRemoved ? 6 : 5"
      class="tw-flex tw-space-x-4 tw-items-center tw-cursor-pointer"
      @click="toggleDetails"
    >
      <span class="tw-text-grey-dark tw-font-bold">{{ backlink.competitorCount }}</span>
      <svg-vue
        icon="icons/chevron"
        class="tw-h-3 tw-w-3 tw-text-grey tw-transition-transform duration-500"
        :class="{
          'tw-rotate-180': showDetails
        }"
      />
    </TableCell>

    <MoveToOpportunitiesButton
      v-if="!isTabOpportunities"
      :disabled="isPending"
      :loading="loadingStatus === 'pending'"
      @click.prevent="updateOpportunityStatus('pending')"
    />

    <MoveToInProgressButton
      v-if="!isTabInProgress"
      :disabled="isPending"
      :loading="loadingStatus === 'contacted'"
      @click.prevent="updateOpportunityStatus('contacted')"
    />

    <ManuallyCompleteButton
      v-if="isTabCompleted"
      :disabled="isPending"
      :backlink="backlink"
    />

    <MoveToCompleteButton
      v-if="!isTabCompleted && !isTabRemoved"
      :disabled="isPending"
      :loading="loadingStatus === 'completed'"
      @click.prevent="updateOpportunityStatus('completed')"
    />

    <MoveToRemovedButton
      v-if="!isTabRemoved && !isTabCompleted"
      :disabled="isPending"
      :loading="loadingStatus === 'removed'"
      @click.prevent="updateOpportunityStatus('removed')"
    />

    <template #accordion>
      <div
        v-show="showDetails"
        class="tw-transition-all tw-border tw-border-t-0 tw-grid tw-grid-cols-12"
      >
        <div class="tw-col-span-4" />
        <div
          class="tw-space-y-4 tw-my-4"
          :class="[
            isTabRemoved ? 'tw-col-span-6' : 'tw-col-span-5'
          ]"
        >
          <LinkExample
            v-for="(example, index) in backlink.linkExamples"
            :key="example.domain"
            :link="example"
            :variant="index % 2 === 0 ? 'blue' : 'purple'"
          />
        </div>
      </div>
    </template>
  </ExpandableTableRow>
</template>
